import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-md"
      >
        <div className="relative aspect-square overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {article.isExclusive && (
            <span className="absolute top-2 left-2 px-2 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-md">
              独家
            </span>
          )}
          {article.isHot && (
            <span className="absolute top-2 right-2 px-2 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
              热榜
            </span>
          )}
        </div>
        <div className="p-3 bg-foreground-950">
          <h3 className="text-sm font-semibold text-background-50 line-clamp-2 mb-1 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-xs text-background-400">{article.publishDate}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-md"
      >
        <div className="w-32 sm:w-40 h-24 sm:h-28 flex-shrink-0 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 py-3 pr-3 min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-1 mb-2">{article.summary}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all hover:shadow-md"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          {article.isExclusive && (
            <span className="px-2 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-md">
              独家
            </span>
          )}
          {article.isHot && (
            <span className="px-2 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
              热榜
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.summary}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-3">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-3-line" />
              {article.commentCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}